import React, { useState, useMemo, useCallback } from 'react';
import { X, Users, Split, DollarSign, Percent, Plus, Minus, Check, ChevronDown, Trash2, Share2 } from 'lucide-react';

const PERSON_COLORS = [
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300', dot: 'bg-teal-500' },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300', dot: 'bg-violet-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' },
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300', dot: 'bg-rose-500' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', dot: 'bg-orange-500' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300', dot: 'bg-cyan-500' },
];

/**
 * SplitBillModal — Industry-standard bill splitting
 *
 * Supports:
 * - By Item: assign items to checks, share items across multiple people (fractional split)
 * - Equal Split: divide total equally among N people
 * - Custom Amount: manually enter amount per person
 *
 * Creates separate checks (bills), not split payments on one bill.
 */
export default function SplitBillModal({ items = [], finalAmount = 0, taxAmount = 0, taxPercentage = 0, cgstRate = 0, sgstRate = 0, onConfirm, onClose }) {
  const [activeTab, setActiveTab] = useState('byItem');
  const [personCount, setPersonCount] = useState(2);

  // ─── By Item: assignments ────────────────────────────────────
  // Each item can be assigned to multiple people (shared)
  // Structure: { itemIdx: { personIdx: fraction } }
  // e.g., { 0: { 0: 1 } } = item 0 fully to person 0
  // e.g., { 1: { 0: 0.5, 1: 0.5 } } = item 1 shared 50-50
  const [itemAssignments, setItemAssignments] = useState({});
  const [showShareModal, setShowShareModal] = useState(null); // itemIdx or null

  // ─── Custom: per-person amounts / percentages ───────────────
  const [customMode, setCustomMode] = useState('amount'); // 'amount' or 'percentage'
  const [customSplits, setCustomSplits] = useState([
    { label: 'Person 1', amount: '', percentage: '' },
    { label: 'Person 2', amount: '', percentage: '' },
  ]);

  const persons = useMemo(() =>
    Array.from({ length: personCount }, (_, i) => `Person ${i + 1}`),
    [personCount]
  );

  const getColor = (idx) => PERSON_COLORS[idx % PERSON_COLORS.length];

  // ─── Assign whole item to one person ─────────────────────────
  const assignItemToPerson = (itemIdx, personIdx) => {
    setItemAssignments(prev => ({
      ...prev,
      [itemIdx]: personIdx === '' ? undefined : { [parseInt(personIdx)]: 1 },
    }));
  };

  // ─── Share item among selected people equally ────────────────
  const shareItemAmong = (itemIdx, personIndices) => {
    if (personIndices.length === 0) {
      setItemAssignments(prev => { const n = { ...prev }; delete n[itemIdx]; return n; });
      return;
    }
    const fraction = Math.round((1 / personIndices.length) * 100) / 100;
    const lastFraction = Math.round((1 - fraction * (personIndices.length - 1)) * 100) / 100;
    const assignment = {};
    personIndices.forEach((pi, i) => {
      assignment[pi] = i === personIndices.length - 1 ? lastFraction : fraction;
    });
    setItemAssignments(prev => ({ ...prev, [itemIdx]: assignment }));
    setShowShareModal(null);
  };

  // ─── Per-check summary (By Item) ────────────────────────────
  const checkSummaries = useMemo(() => {
    const checks = {};
    persons.forEach((_, pi) => { checks[pi] = { items: [], subtotal: 0 }; });

    items.forEach((item, idx) => {
      const assignment = itemAssignments[idx];
      if (!assignment) return; // unassigned

      Object.entries(assignment).forEach(([personIdx, fraction]) => {
        const pi = parseInt(personIdx);
        if (!checks[pi]) checks[pi] = { items: [], subtotal: 0 };
        const itemTotal = (item.total || item.price * item.quantity) * fraction;
        checks[pi].items.push({
          ...item,
          idx,
          fraction,
          splitQuantity: item.quantity * fraction,
          splitTotal: itemTotal,
        });
        checks[pi].subtotal += itemTotal;
      });
    });

    // Calculate tax proportionally per check based on subtotal ratio
    const totalSubtotal = Object.values(checks).reduce((sum, c) => sum + c.subtotal, 0);
    Object.values(checks).forEach(check => {
      const ratio = totalSubtotal > 0 ? check.subtotal / totalSubtotal : 0;
      check.tax = Math.round(taxAmount * ratio * 100) / 100;
      check.total = Math.round(check.subtotal + check.tax);
    });

    return checks;
  }, [items, itemAssignments, persons, taxAmount]);

  const unassignedItems = items.filter((_, idx) => !itemAssignments[idx]);
  const itemSplitValid = unassignedItems.length === 0 && items.length > 0;

  // ─── Equal Split ─────────────────────────────────────────────
  const equalPerPerson = Math.floor(finalAmount / personCount);
  const equalLastPerson = finalAmount - equalPerPerson * (personCount - 1);

  // ─── Custom ──────────────────────────────────────────────────
  const customTotalPercent = customSplits.reduce((sum, s) => sum + (parseFloat(s.percentage) || 0), 0);
  const customTotal = customMode === 'percentage'
    ? customSplits.reduce((sum, s) => sum + Math.round(finalAmount * (parseFloat(s.percentage) || 0) / 100), 0)
    : customSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const customRemaining = Math.round(finalAmount - customTotal);

  const changePersonCount = (newCount) => {
    const count = Math.max(2, Math.min(8, newCount));
    setPersonCount(count);
    if (count < personCount) {
      // Remove assignments for removed persons
      setItemAssignments(prev => {
        const cleaned = {};
        Object.entries(prev).forEach(([itemIdx, assignment]) => {
          if (!assignment) return;
          const filtered = {};
          Object.entries(assignment).forEach(([pi, frac]) => {
            if (parseInt(pi) < count) filtered[pi] = frac;
          });
          if (Object.keys(filtered).length > 0) cleaned[itemIdx] = filtered;
        });
        return cleaned;
      });
    }
    if (activeTab === 'custom') {
      setCustomSplits(prev => {
        if (count > prev.length) {
          return [...prev, ...Array.from({ length: count - prev.length }, (_, i) => ({
            label: `Person ${prev.length + i + 1}`, amount: '', percentage: ''
          }))];
        }
        return prev.slice(0, count);
      });
    }
  };

  // ─── Get assignment display for an item ──────────────────────
  const getItemAssignmentDisplay = (idx) => {
    const assignment = itemAssignments[idx];
    if (!assignment) return null;
    const entries = Object.entries(assignment);
    if (entries.length === 1 && entries[0][1] === 1) {
      // Single person, full item
      const pi = parseInt(entries[0][0]);
      return { type: 'single', personIdx: pi, label: persons[pi] };
    }
    // Shared
    return {
      type: 'shared',
      people: entries.map(([pi, frac]) => ({ personIdx: parseInt(pi), fraction: frac, label: persons[parseInt(pi)] })),
    };
  };

  // ─── Confirm ─────────────────────────────────────────────────
  const handleConfirm = () => {
    let splits = [];

    if (activeTab === 'byItem') {
      if (!itemSplitValid) return;
      persons.forEach((label, pi) => {
        const check = checkSummaries[pi];
        if (check && check.items.length > 0) {
          splits.push({
            label,
            amount: check.total,
            subtotal: Math.round(check.subtotal * 100) / 100,
            tax: Math.round(check.tax * 100) / 100,
            items: check.items.map(it => ({
              name: it.name,
              quantity: it.splitQuantity,
              price: it.price,
              total: Math.round(it.splitTotal * 100) / 100,
              menuItemId: it.menuItemId,
              variantId: it.variantId,
            })),
          });
        }
      });
    } else if (activeTab === 'equal') {
      persons.forEach((label, pi) => {
        const amount = pi === personCount - 1 ? equalLastPerson : equalPerPerson;
        splits.push({ label, amount, subtotal: amount, tax: 0, items: [] });
      });
    } else if (activeTab === 'custom') {
      if (Math.abs(customRemaining) > 1) return;
      customSplits.forEach(s => {
        const amount = customMode === 'percentage'
          ? Math.round(finalAmount * (parseFloat(s.percentage) || 0) / 100)
          : (parseFloat(s.amount) || 0);
        if (amount > 0) {
          splits.push({ label: s.label, amount, subtotal: amount, tax: 0, items: [] });
        }
      });
    }

    onConfirm(splits);
  };

  const canConfirm = activeTab === 'byItem' ? itemSplitValid
    : activeTab === 'equal' ? true
    : customMode === 'percentage'
      ? (Math.abs(customTotalPercent - 100) <= 1 && customTotalPercent > 0)
      : (Math.abs(customRemaining) <= 1 && customTotal > 0);

  const tabs = [
    { id: 'byItem', label: 'By Item', icon: Split },
    { id: 'equal', label: 'Equal', icon: Users },
    { id: 'custom', label: 'Custom', icon: DollarSign },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold">Split Bill</h2>
            <p className="text-xs text-slate-300">Total: ₹{finalAmount} • {items.length} items</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition border-b-2 ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-700 bg-teal-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Person Count */}
        <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200 flex-shrink-0">
          <span className="text-sm font-medium text-slate-700">People</span>
          <div className="flex items-center gap-3">
            {/* Person color dots */}
            <div className="flex gap-1">
              {persons.map((_, pi) => (
                <div key={pi} className={`w-5 h-5 rounded-full ${getColor(pi).dot} flex items-center justify-center text-white text-[9px] font-bold`}>
                  {pi + 1}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => changePersonCount(personCount - 1)} disabled={personCount <= 2}
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 transition">
                <Minus size={12} />
              </button>
              <span className="w-6 text-center font-bold">{personCount}</span>
              <button onClick={() => changePersonCount(personCount + 1)} disabled={personCount >= 8}
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 transition">
                <Plus size={12} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* ─── BY ITEM ─── */}
          {activeTab === 'byItem' && (
            <div className="space-y-2">
              {unassignedItems.length > 0 && (
                <div className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-2 rounded-lg">
                  {unassignedItems.length} item{unassignedItems.length > 1 ? 's' : ''} not assigned
                </div>
              )}

              {items.map((item, idx) => {
                const display = getItemAssignmentDisplay(idx);
                const color = display?.type === 'single' ? getColor(display.personIdx) : null;

                return (
                  <div key={idx} className={`p-3 border rounded-lg transition ${
                    display ? `${color?.bg || 'bg-violet-50'} ${color?.border || 'border-violet-200'}` : 'bg-white border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">{item.quantity} × ₹{item.price?.toFixed(0)} = ₹{item.total?.toFixed(0)}</p>
                      </div>

                      <div className="flex items-center gap-1">
                        {/* Assign dropdown */}
                        <select
                          value={display?.type === 'single' ? display.personIdx : ''}
                          onChange={(e) => assignItemToPerson(idx, e.target.value)}
                          className="appearance-none pl-2 pr-6 py-1.5 text-xs font-semibold rounded border bg-white border-slate-300 cursor-pointer min-w-[80px]"
                        >
                          <option value="">Assign</option>
                          {persons.map((label, pi) => (
                            <option key={pi} value={pi}>{label}</option>
                          ))}
                        </select>

                        {/* Share button */}
                        <button
                          onClick={() => setShowShareModal(idx)}
                          className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded transition"
                          title="Share this item among multiple people"
                        >
                          <Share2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Shared indicator */}
                    {display?.type === 'shared' && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {display.people.map(p => (
                          <span key={p.personIdx} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getColor(p.personIdx).bg} ${getColor(p.personIdx).text}`}>
                            {p.label} ({Math.round(p.fraction * 100)}%)
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Per-check summary */}
              {itemSplitValid && (
                <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Checks</p>
                  {persons.map((label, pi) => {
                    const check = checkSummaries[pi];
                    if (!check || check.items.length === 0) return null;
                    return (
                      <div key={pi} className={`flex items-center justify-between px-3 py-2 rounded-lg ${getColor(pi).bg} ${getColor(pi).border} border`}>
                        <div>
                          <span className={`text-sm font-semibold ${getColor(pi).text}`}>{label}</span>
                          <span className="text-xs text-slate-500 ml-2">{check.items.length} items</span>
                        </div>
                        <span className={`text-sm font-bold ${getColor(pi).text}`}>₹{check.total}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── EQUAL ─── */}
          {activeTab === 'equal' && (
            <div className="space-y-3">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                <p className="text-sm text-teal-700">Each person pays</p>
                <p className="text-3xl font-black text-teal-800 mt-1">₹{equalPerPerson}</p>
                {equalPerPerson !== equalLastPerson && (
                  <p className="text-xs text-teal-600 mt-1">Last person pays ₹{equalLastPerson} (rounding)</p>
                )}
              </div>
              <div className="space-y-1.5">
                {persons.map((label, pi) => (
                  <div key={pi} className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${getColor(pi).bg} ${getColor(pi).border}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full ${getColor(pi).dot} flex items-center justify-center text-white text-[9px] font-bold`}>{pi+1}</div>
                      <span className={`text-sm font-semibold ${getColor(pi).text}`}>{label}</span>
                    </div>
                    <span className={`text-sm font-bold ${getColor(pi).text}`}>₹{pi === personCount - 1 ? equalLastPerson : equalPerPerson}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── CUSTOM ─── */}
          {activeTab === 'custom' && (
            <div className="space-y-3">
              {/* Mode Toggle: Amount vs Percentage */}
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <button
                  onClick={() => setCustomMode('amount')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition ${
                    customMode === 'amount' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  <DollarSign size={13} /> By Amount
                </button>
                <button
                  onClick={() => setCustomMode('percentage')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition ${
                    customMode === 'percentage' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  <Percent size={13} /> By Percentage
                </button>
              </div>

              {/* Percentage Presets */}
              {customMode === 'percentage' && customSplits.length === 2 && (
                <div className="flex gap-1.5">
                  {[
                    { label: '50-50', values: [50, 50] },
                    { label: '60-40', values: [60, 40] },
                    { label: '70-30', values: [70, 30] },
                    { label: '75-25', values: [75, 25] },
                  ].map(preset => (
                    <button key={preset.label}
                      onClick={() => setCustomSplits(prev => prev.map((s, i) => ({ ...s, percentage: String(preset.values[i] || 0) })))}
                      className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition">
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Per-person inputs */}
              {customSplits.map((split, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${getColor(idx).dot} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>{idx+1}</div>
                  <input type="text" value={split.label}
                    onChange={(e) => setCustomSplits(prev => prev.map((s, i) => i === idx ? { ...s, label: e.target.value } : s))}
                    className="w-24 px-2 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-teal-500" placeholder="Name" />

                  {customMode === 'percentage' ? (
                    <div className="relative flex-1">
                      <input type="number" min="0" max="100" step="1" value={split.percentage}
                        onChange={(e) => setCustomSplits(prev => prev.map((s, i) => i === idx ? { ...s, percentage: e.target.value } : s))}
                        className="w-full pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-teal-500" placeholder="0" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  ) : (
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                      <input type="number" min="0" step="1" value={split.amount}
                        onChange={(e) => setCustomSplits(prev => prev.map((s, i) => i === idx ? { ...s, amount: e.target.value } : s))}
                        className="w-full pl-7 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-teal-500" placeholder="0" />
                    </div>
                  )}

                  {/* Calculated amount for percentage mode */}
                  {customMode === 'percentage' && (parseFloat(split.percentage) || 0) > 0 && (
                    <span className="text-xs font-semibold text-slate-500 w-16 text-right">
                      ₹{Math.round(finalAmount * (parseFloat(split.percentage) || 0) / 100)}
                    </span>
                  )}

                  {customSplits.length > 2 && (
                    <button onClick={() => { setCustomSplits(prev => prev.filter((_, i) => i !== idx)); }}
                      className="p-1.5 text-red-400 hover:text-red-600 rounded transition"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}

              <button onClick={() => setCustomSplits(prev => [...prev, { label: `Person ${prev.length + 1}`, amount: '', percentage: '' }])}
                className="w-full py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition flex items-center justify-center gap-1.5">
                <Plus size={14} /> Add Person
              </button>

              {/* Remaining / Total indicator */}
              {customMode === 'percentage' ? (
                <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${
                  Math.abs(customTotalPercent - 100) <= 1 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-sm font-bold">{customTotalPercent}% {Math.abs(customTotalPercent - 100) <= 1 ? '✓' : `(need 100%)`}</span>
                </div>
              ) : (
                <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${
                  Math.abs(customRemaining) <= 1 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <span className="text-sm font-medium">Remaining</span>
                  <span className="text-sm font-bold">₹{customRemaining}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-white flex-shrink-0 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm transition">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={!canConfirm}
            className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2">
            <Check size={16} />
            Split into {activeTab === 'byItem'
              ? `${Object.values(checkSummaries).filter(c => c.items.length > 0).length} checks`
              : activeTab === 'equal' ? `${personCount} checks` : `${customSplits.filter(s => customMode === 'percentage' ? parseFloat(s.percentage) > 0 : parseFloat(s.amount) > 0).length} checks`}
          </button>
        </div>
      </div>

      {/* ─── Share Item Modal ─── */}
      {showShareModal !== null && (
        <ShareItemModal
          item={items[showShareModal]}
          persons={persons}
          colors={PERSON_COLORS}
          currentAssignment={itemAssignments[showShareModal]}
          onConfirm={(personIndices) => shareItemAmong(showShareModal, personIndices)}
          onClose={() => setShowShareModal(null)}
        />
      )}
    </div>
  );
}

/**
 * Sub-modal for sharing a single item among multiple people
 */
function ShareItemModal({ item, persons, colors, currentAssignment, onConfirm, onClose }) {
  const [selected, setSelected] = useState(() => {
    if (!currentAssignment) return [];
    return Object.keys(currentAssignment).map(k => parseInt(k));
  });

  const toggle = (pi) => {
    setSelected(prev => prev.includes(pi) ? prev.filter(p => p !== pi) : [...prev, pi]);
  };

  const fraction = selected.length > 0 ? Math.round((1 / selected.length) * 100) : 0;
  const perPersonAmount = selected.length > 0 ? ((item.total || 0) / selected.length).toFixed(0) : 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xs overflow-hidden">
        <div className="bg-violet-600 text-white px-4 py-3">
          <h3 className="text-sm font-bold">Share Item</h3>
          <p className="text-xs text-violet-200 mt-0.5">{item.name} • ₹{item.total?.toFixed(0)}</p>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-xs text-slate-500 font-medium">Select who shares this item:</p>
          {persons.map((label, pi) => {
            const isSelected = selected.includes(pi);
            const color = colors[pi % colors.length];
            return (
              <button key={pi} onClick={() => toggle(pi)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition text-left ${
                  isSelected ? `${color.bg} ${color.border} ${color.text}` : 'border-slate-200 hover:border-slate-300'
                }`}>
                <div className={`w-5 h-5 rounded-full ${isSelected ? color.dot : 'bg-slate-300'} flex items-center justify-center`}>
                  {isSelected && <Check size={12} className="text-white" />}
                </div>
                <span className="text-sm font-medium flex-1">{label}</span>
                {isSelected && <span className="text-xs font-bold">{fraction}% • ₹{perPersonAmount}</span>}
              </button>
            );
          })}
        </div>
        <div className="border-t border-slate-200 p-3 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium">Cancel</button>
          <button onClick={() => onConfirm(selected)} disabled={selected.length === 0}
            className="flex-1 py-2 bg-violet-600 text-white rounded-lg text-sm font-bold disabled:opacity-50">
            {selected.length > 0 ? `Split ${selected.length} ways` : 'Select people'}
          </button>
        </div>
      </div>
    </div>
  );
}
